'use client';

import { createTheme } from '@mui/material/styles';

// TV4 Play brand palette — Red (primary)
const tv4Red = {
  red01: '#E0001C',
  red02: '#C40317',
  red03: '#99000F',
  red04: '#750308',
  red05: '#5C0505',
  red06: '#400500',
  red07: '#2E0400',
  red08: '#1C0200',
} as const;

// TV4 Play brand palette — Grey (secondary)
const tv4Grey = {
  grey01: '#4A4A4D',
  grey02: '#363639',
  grey03: '#2A2A2D',
  grey04: '#1F1F21',
  grey05: '#171719',
  grey06: '#0D0D0F',
} as const;

const tv4Theme = createTheme({
  breakpoints: {
    values: {
      mobile: 430,
      xs: 550,
      sm: 800,
      readableText: 900,
      md: 1100,
      mdlg: 1300,
      lg: 1440,
      xl: 1536,
      xxl: 1800, // new breakpoint value
      headerAlignment: 1370,
      navlinkAlignment: 1350,
      heroVideoBreakpoint: 1000,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: tv4Red.red01,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FFFFFF',
    },
    background: {
      default: '#f4f2f1',
      paper: '#242424',
      white: '#FFFFFF',
      lightGrey: '#f4f2f1',
      dark: '#1b1b1b',
      grey: '#d9d9d9',
      darkGrey: '#7a7a7a',
      activeTab: '#710923',
      cardCtaBackground: tv4Grey.grey01,
      separator: tv4Grey.grey06,
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
      dark: '#191817',
      link: tv4Red.red02,
      linkSecondary: '#f66'
    },
    form: {
      textFieldBorder: '#c0c0c0',
      // 7:1 contrast on white — meets WCAG AAA for normal text (#5F5F5F vs #FFFFFF).
      formLabel: '#5F5F5F'
    },
    tv4Red,
    tv4Grey,
  },
  shape: {
    borderRadiusFull: 50,
    borderRadiusLg: 9,
    borderRadiusMd: 6,
    borderRadiusSm: 3,
  },
  typography: {
    fontFamily: 'var(--fyran-sans), "Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightSemiBold: 550,

    h1: {
      fontWeight: 700,
      fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 700,
      fontSize: 'clamp(2rem, 3.5vw, 3rem)',
      lineHeight: 1.15,
    },
    h3: {
      fontWeight: 600,
      fontSize: 'clamp(1.5625rem, 2.75vw, 2.25rem)',
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 600,
      fontSize: 'clamp(1.125rem, 2.3vw, 1.4375rem)',
      lineHeight: 1.25,
    },
    h5: {
      fontWeight: 600,
      fontSize: 'clamp(0.875rem, 2vw, 1.1875rem)',
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      fontSize: 'clamp(0.800rem, 1.9vw, 1.0345rem)',
      lineHeight: 1.35,
    },
    button: {
      fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
      lineHeight: 1.35,
      textTransform: 'none' as const,
      fontWeight: 600,
    },
    body1: {
      fontSize: 'clamp(0.925rem, 1.6vw, 1.125rem)',
      lineHeight: 1.35,
    },
    body2: {
      fontSize: 'clamp(0.825rem, 1.6vw, 0.95rem)',
      lineHeight: 1.35,
    },
    caption: {
      fontSize: 'clamp(0.625rem, 1.6vw, 0.8rem)',
      lineHeight: 1.25,
    },
    tabs: {
      fontSize: 'clamp(0.85rem, 1.6vw, 1rem)',
      lineHeight: 1.35,
    },
    navLink: {
      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
      lineHeight: 1.35,
      fontWeight: 400,
    },
    logoLink: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
      fontWeight: 700,
      lineHeight: 1.35,
    },
    overline: {
      fontFamily: 'var(--fyran-sans), "Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)',
      fontWeight: 500,
      lineHeight: 1.35,
      textTransform:'none'
    },
    footerGroup: {
      heading: {
        fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      link: {
        fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)',
        fontWeight: 400,
        lineHeight: 1.35,
      }
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '3.125rem',
          textTransform: 'none',
          fontWeight: 600,
          padding: '7px 24px',
        },
      },
      variants: [
        {
          props: {variant: 'contained', color: 'primary'},
          style: {
            '&:hover': {
              backgroundColor: '#C8001F',
            },
          },
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#2A2A2A',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1A1A',
        },
      },
    },
  },
});

export default tv4Theme;